/**
 * WCAG 2.1.4 — Character Key Shortcuts
 *
 * Shopify-admin-style keyboard shortcuts for the Marou storefront.
 * Single-key + multi-key sequences (1s window) like `G H`, `B Y E`.
 *
 * Compliance:
 *  - Off by default. User must opt in via the help modal (`?`).
 *    This satisfies 2.1.4(a) — turn off mechanism.
 *  - The `?` help shortcut is always live so users can discover the
 *    feature and toggle it; this is the documented turn-off entry point.
 *  - Suppressed when focus is in a text input / editable surface.
 *  - Suppressed when any modifier key is held (Ctrl/Cmd/Alt) so we never
 *    capture browser / OS shortcuts.
 *
 * Single-key:
 *  ?  show help
 *  s  open search drawer
 *  f  focus first filter on a collection page
 *
 * Sequences (type each letter within 1s):
 *  g h    go home
 *  g c    go to cart
 *  g s    go to all products
 *  g a    go to account
 *  g o    go to account orders
 *  g b    go to blog
 *  g l    log in
 *  a c    add current product to cart (PDP)
 *  b y e  log out
 */
(() => {
  const STORAGE_KEY = 'marou.kbd-shortcuts.enabled';
  const HELP_ID = 'KeyboardShortcutsHelp';
  const SEQUENCE_WINDOW_MS = 1000;

  const isEnabled = () => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; }
    catch (_) { return false; }
  };

  const setEnabled = (on) => {
    try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); }
    catch (_) {}
    syncCheckbox();
  };

  const isEditable = (el) => {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
  };

  const click = (el) => { if (el && typeof el.click === 'function') el.click(); };

  const openSearch = () => click(document.querySelector('.thb-quick-search'));

  const openCart = () => {
    const btn = document.getElementById('cart-drawer-toggle')
      || document.querySelector('[aria-controls="Cart-Drawer"]')
      || document.querySelector('a[href="/cart"]');
    click(btn);
  };

  const focusFilter = () => {
    const target = document.querySelector('#main-collection-filters input, .facets__form input, .filter input, [data-filter]');
    if (target && typeof target.focus === 'function') target.focus();
  };

  const addToCart = () => {
    const btn = document.querySelector('button[type="submit"][name="add"]')
      || document.querySelector('form[action*="/cart/add"] button[type="submit"]');
    if (btn && !btn.disabled) click(btn);
  };

  const go = (path) => { window.location.assign(path); };

  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const focusables = (panel) =>
    Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => el.offsetParent !== null);

  const closeOtherPanels = () => {
    document.querySelectorAll('.side-panel.active').forEach((p) => {
      if (p.id !== HELP_ID) p.classList.remove('active');
    });
  };

  const openHelp = () => {
    const panel = document.getElementById(HELP_ID);
    if (!panel) return;
    closeOtherPanels();
    panel.classList.add('active');
    document.body.classList.add('open-cc');
    const list = focusables(panel);
    if (list.length) list[0].focus();
    document.addEventListener('keydown', onPanelKey, true);
  };

  const closeHelp = () => {
    const panel = document.getElementById(HELP_ID);
    if (!panel) return;
    panel.classList.remove('active');
    document.body.classList.remove('open-cc');
    document.removeEventListener('keydown', onPanelKey, true);
  };

  // Only handle Tab trap here. Escape is owned by app.js (dispatches
  // 'panel:close' which removes .active on whatever panel is open).
  // A MutationObserver below detaches our keydown listener when the
  // panel goes inactive.
  const onPanelKey = (e) => {
    const panel = document.getElementById(HELP_ID);
    if (!panel || !panel.classList.contains('active')) return;
    if (e.key !== 'Tab') return;
    const list = focusables(panel);
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  const syncCheckbox = () => {
    const cb = document.getElementById('kbd-shortcuts-toggle');
    if (cb) cb.checked = isEnabled();
  };

  /* ---------- shortcut tables ---------- */

  // Single-key shortcuts (always run a function).
  // `?` is in `singles` but exempt from the enabled gate — see onKey().
  const singles = {
    '?': openHelp,
    's': openSearch,
    'f': focusFilter
  };

  // Multi-key sequences — keys are typed in order within SEQUENCE_WINDOW_MS.
  const sequences = {
    'gh': () => go('/'),
    'gc': () => go('/cart'),
    'gs': () => go('/collections/all'),
    'ga': () => go('/account'),
    'go': () => go('/account'),
    'gb': () => go('/blogs/news'),
    'gl': () => go('/account/login'),
    'ac': addToCart,
    'bye': () => go('/account/logout')
  };

  // Any sequence prefix that could still resolve — used to know whether to
  // keep buffering or to discard immediately.
  const seqPrefixes = new Set();
  Object.keys(sequences).forEach((seq) => {
    for (let i = 1; i <= seq.length; i++) seqPrefixes.add(seq.slice(0, i));
  });

  let buffer = '';
  let bufferTimer = 0;

  const resetBuffer = () => { buffer = ''; clearTimeout(bufferTimer); bufferTimer = 0; };

  const armResetTimer = () => {
    clearTimeout(bufferTimer);
    bufferTimer = setTimeout(resetBuffer, SEQUENCE_WINDOW_MS);
  };

  const onKey = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (isEditable(document.activeElement)) return;

    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

    // `?` always live (turn-off discoverability per 2.1.4)
    if (key === '?') {
      e.preventDefault();
      singles['?']();
      resetBuffer();
      return;
    }

    if (!isEnabled()) return;

    // Ignore non-printable / multi-char keys (Escape, Tab, F1, etc.)
    if (key.length !== 1) { resetBuffer(); return; }
    if (!/^[a-z0-9/]$/.test(key)) { resetBuffer(); return; }

    const next = buffer + key;

    // If the extended buffer resolves a sequence, fire it.
    if (sequences[next]) {
      e.preventDefault();
      sequences[next]();
      resetBuffer();
      return;
    }

    // If the extended buffer is still a viable prefix of a sequence,
    // keep buffering and wait for the next key.
    if (seqPrefixes.has(next)) {
      e.preventDefault();
      buffer = next;
      armResetTimer();
      return;
    }

    // Buffer doesn't extend a sequence. Try as a single-key shortcut
    // (only when there is no in-progress sequence to disambiguate).
    if (buffer === '' && singles[key]) {
      e.preventDefault();
      singles[key]();
      return;
    }

    // No match — reset and let the keystroke fall through.
    resetBuffer();
  };

  const init = () => {
    syncCheckbox();
    document.addEventListener('keydown', onKey);

    document.querySelectorAll('[data-kbd-open]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => { e.preventDefault(); openHelp(); });
    });

    const panel = document.getElementById(HELP_ID);
    if (panel) {
      // Keep our Tab-trap listener in sync with the active state.
      // app.js closes the panel on Escape / overlay click; we observe
      // .active being removed and detach the Tab handler accordingly.
      new MutationObserver(() => {
        if (!panel.classList.contains('active')) {
          document.removeEventListener('keydown', onPanelKey, true);
          document.body.classList.remove('open-cc');
        }
      }).observe(panel, { attributes: true, attributeFilter: ['class'] });

      const cb = panel.querySelector('#kbd-shortcuts-toggle');
      if (cb) cb.addEventListener('change', (e) => setEnabled(e.target.checked));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
